import React, { useState, useEffect } from 'react';
import SelectBuscable from '@/shared/components/SelectBuscable';
import { Badge } from '@/shared/components/Badge';
import { ArrowLeft, Download, Search, Calendar, Loader2, Upload, RefreshCw } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { supplierPaymentsService } from '@/features/proveedores/api/supplierPayments.service';
import type { SupplierPayment } from '@/types/supplierPayment.types';
import { UploadComplementModal } from '@/features/proveedores/components/UploadComplementModal';
import toast from 'react-hot-toast';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

export const SupplierPayments: React.FC = () => {
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const [searchTerm, setSearchTerm] = useState('');
  const [payments, setPayments] = useState<SupplierPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<SupplierPayment | null>(null);
  const [isReplacing, setIsReplacing] = useState(false);

  useEffect(() => {
    fetchPayments();
  }, [orderId, searchTerm, statusFilter, fromDate, toDate, paymentMethodFilter]);

  const fetchPayments = async () => {
    if (!orderId) return;

    try {
      setLoading(true);
      setError(null);
      const allPayments = await supplierPaymentsService.getAllPaymentsByOrder(orderId, {
        search: searchTerm || undefined,
        payment_method: paymentMethodFilter || undefined,
        status: statusFilter as any || undefined,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
      });
      setPayments(allPayments);
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error fetching payments:', err);
      setError(err.message || 'Error al cargar los pagos');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setPaymentMethodFilter('');
    setFromDate('');
    setToDate('');
  };

  const handleExport = async () => {
    if (!orderId) return;
    await supplierPaymentsService.exportToExcel(orderId);
  };

  const openUploadModal = (payment: SupplierPayment, replacing: boolean = false) => {
    setSelectedPayment(payment);
    setIsReplacing(replacing);
    setUploadModalOpen(true);
  };

  const handleUploadComplement = async (xmlFile: File, pdfFile?: File) => {
    if (!orderId || !selectedPayment) return;

    try {
      await supplierPaymentsService.uploadComplement(
        orderId,
        selectedPayment.id,
        xmlFile,
        pdfFile
      );

      toast.success('Complemento subido exitosamente', {
        duration: 4000,
        position: 'bottom-right',
      });

      // Recargar pagos para ver el nuevo complemento
      await fetchPayments();
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      toast.error(error.message || 'Error al subir el complemento', {
        duration: 5000,
        position: 'bottom-right',
      });
      throw error;
    }
  };

  const hasActiveFilters = searchTerm || statusFilter || paymentMethodFilter || fromDate || toDate;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <button
            onClick={() => navigate('/proveedores/ordenes')}
            className={estiloDeBoton('secundario', 'mb-3')}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver a órdenes de compra
          </button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Pagos
          </h1>
          <p className="text-gray-600">
            Consulta aquí todos los pagos de esta orden
          </p>
        </div>
        <button
          onClick={handleExport}
          className={estiloDeBoton('secundario')}
        >
          <Download className="w-4 h-4" />
          Exportar a excel
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-3">
          {/* Search */}
          <div className="flex-1 min-w-[250px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por factura, referencia..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Payment Method Filter */}
          <SelectBuscable
            variante="listado"
            ancho="contenido"
            etiqueta="Método de pago"
            todos="Todos"
            value={paymentMethodFilter}
            onChange={setPaymentMethodFilter}
            opciones={[
              { value: 'Transferencia bancaria', label: 'Transferencia' },
              { value: 'Cheque', label: 'Cheque' },
              { value: 'Efectivo', label: 'Efectivo' },
            ]}
            canDelete={false}
            placeholderDeBusqueda="Buscar método..."
          />

          {/* Status Filter */}
          <SelectBuscable
            variante="listado"
            ancho="contenido"
            etiqueta="Estado"
            todos="Todos"
            value={statusFilter}
            onChange={setStatusFilter}
            opciones={[
              { value: 'pending', label: 'Pendiente' },
              { value: 'paid', label: 'Pagado' },
            ]}
            canDelete={false}
            placeholderDeBusqueda="Buscar estado..."
          />

          {/* Date From */}
          <div className="relative">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-40 pl-3 pr-10 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-500 pointer-events-none" />
          </div>

          {/* Date To */}
          <div className="relative">
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-40 pl-3 pr-10 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-500 pointer-events-none" />
          </div>

          {/* Clear Button */}
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg font-medium transition-colors"
            >
              Limpiar
            </button>
          )}
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#E5E7EB]">
            <thead className="bg-[#FAF9FF]">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Factura
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Monto
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Fecha de pago
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Método
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Referencia
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Complemento
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E1E1E1]">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center gap-2 text-gray-500">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Cargando pagos...</span>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="text-red-600">{error}</div>
                  </td>
                </tr>
              ) : payments.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="text-gray-500">
                      {hasActiveFilters
                        ? 'No se encontraron pagos con los filtros aplicados'
                        : 'No hay pagos registrados para esta orden'
                      }
                    </div>
                  </td>
                </tr>
              ) : (
                payments.map((payment) => (
                  <tr
                    key={payment.id}
                    className="hover:bg-[#F4F7FB] transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {payment.invoice_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      {supplierPaymentsService.formatCurrency(payment.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {supplierPaymentsService.formatDate(payment.payment_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {payment.payment_method}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-mono">
                      {payment.reference_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge color={payment.status === 'paid' ? 'verde' : 'amarillo'}>
                        {payment.status === 'paid' ? 'Pagado' : 'Pendiente'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex flex-col gap-2">
                        {payment.complement_status === 'rejected' && (
                          <div className="mb-1">
                            <Badge color="rojo" className="mb-1">Rechazado</Badge>
                            {payment.complement_validation_notes && (
                              <p className="text-xs text-red-600 max-w-[200px] whitespace-normal">
                                {payment.complement_validation_notes}
                              </p>
                            )}
                          </div>
                        )}

                        {payment.complement_xml_url ? (
                          <div className="flex items-center gap-2">
                            <a
                              href={payment.complement_xml_url}
                              download
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded transition-colors"
                              title="Descargar XML"
                            >
                              <Download className="w-3 h-3" />
                              XML
                            </a>
                            {payment.complement_pdf_url && (
                              <a
                                href={payment.complement_pdf_url}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded transition-colors"
                                title="Descargar PDF"
                              >
                                <Download className="w-3 h-3" />
                                PDF
                              </a>
                            )}
                            <button
                              onClick={() => openUploadModal(payment, true)}
                              className={estiloDeBoton('secundario')}
                              title="Reemplazar complemento"
                            >
                              <RefreshCw className="w-4 h-4" />
                              Reemplazar
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => openUploadModal(payment, false)}
                            className={estiloDeBoton('primario')}
                          >
                            <Upload className="w-4 h-4" />
                            Subir
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload Complement Modal */}
      {selectedPayment && (
        <UploadComplementModal
          isOpen={uploadModalOpen}
          onClose={() => {
            setUploadModalOpen(false);
            setSelectedPayment(null);
            setIsReplacing(false);
          }}
          onSubmit={handleUploadComplement}
          paymentInfo={{
            invoiceNumber: selectedPayment.invoice_number,
            amount: selectedPayment.amount,
            referenceNumber: selectedPayment.reference_number,
          }}
          isReplacing={isReplacing}
        />
      )}
    </div>
  );
};
